import React, { useEffect, useState } from 'react';
import {
  BarList, Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, SubTabs,
  TextInput, fmtDate, panelGet, panelPost, useAdminGet,
} from './kit';
import type { AdminTargetIn } from './adminNav';
import SectorsKeywords from './content/SectorsKeywords';

/**
 * Content & TRL group — the technology-catalog side of the panels:
 *   • catalog stats + university directory + breakdown charts (admin_v3
 *     Universities page) and the Technology / Researcher / Tech-manager
 *     explorers from admin_panel_v2 Tab 13;
 *   • the SQLite catalog status, headline metrics, analytics builder and its
 *     snapshot history (v2 Tab 14);
 *   • the Industry Categories Breakdown (v2:5934-6168);
 *   • Data Quality Stats (v2:6791-7186), TRL Distribution (v2:7190-7516) and
 *     Crawl Quality (v2:13052-13350);
 *   • Sectors & keywords (content/SectorsKeywords.tsx): the live sector /
 *     sub-sector / keyword pivot and insights (Backend/admin_sectors.py).
 *
 * Deep links (admin/adminNav.tsx): /admin?group=content&tab=<the snake_case
 * of a tab label: overview, sectors_keywords (or sectors), universities, …>.
 * Sectors & keywords reads more params of its own (see that file).
 */

const SECTORS = 'Sectors & keywords';
const TABS = ['Overview', SECTORS, 'Universities', 'Technologies', 'Researchers', 'Tech managers',
  'Contact emails', 'Catalog DB', 'Categories', 'Data quality', 'TRL', 'Crawl quality'];
const snake = (t: string) => t.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
/** Deep-link tab names → tabs: each label's snake_case, plus short aliases. */
const TAB_PARAM: Record<string, string> = {
  ...Object.fromEntries(TABS.map(t => [snake(t), t])),
  sectors: SECTORS, pivot: SECTORS, keywords: SECTORS, cosmos: SECTORS,
};

const ContentPanel: React.FC<{ target?: AdminTargetIn }> = ({ target }) => {
  const [tab, setTab] = useState(TABS[0]);
  // Sectors & keywords stays mounted (hidden) once opened, so its pivot and
  // drawers survive a look at another tab; the other tabs remount as before.
  const [sectorsSeen, setSectorsSeen] = useState(false);
  const pick = (t: string) => { setTab(t); if (t === SECTORS) setSectorsSeen(true); };
  const tabOf = (t?: AdminTargetIn) => {
    const raw = String(t?.tab ?? '').trim();
    return TABS.includes(raw) ? raw : TAB_PARAM[snake(raw)];
  };
  useEffect(() => {
    if (!target) return;
    const t = tabOf(target);
    if (t) pick(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.nonce]);
  return (
    <div className="flex flex-col gap-4 min-w-0">
      <SubTabs tabs={TABS} active={tab} onChange={pick} />
      {tab === 'Overview' && <Overview />}
      {sectorsSeen && (
        <div className={tab === SECTORS ? 'flex flex-col gap-4 min-w-0' : 'hidden'}>
          <SectorsKeywords target={tabOf(target) === SECTORS ? target : undefined} />
        </div>
      )}
      {tab === 'Universities' && <Universities />}
      {tab === 'Technologies' && <TechExplorer />}
      {tab === 'Researchers' && <ResearcherExplorer />}
      {tab === 'Tech managers' && <TechManagers />}
      {tab === 'Contact emails' && <ContactEmails />}
      {tab === 'Catalog DB' && <CatalogDb />}
      {tab === 'Categories' && <Categories />}
      {tab === 'Data quality' && <DataQuality />}
      {tab === 'TRL' && <Trl />}
      {tab === 'Crawl quality' && <CrawlQuality />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Small shared helpers
// ---------------------------------------------------------------------------
type Flash = { kind: 'error' | 'ok'; text: string } | null;

const num = (v: any): string =>
  typeof v === 'number' ? v.toLocaleString() : (v === null || v === undefined || v === '' ? '—' : String(v));

const pct = (v: any, dp = 1): string =>
  v === null || v === undefined || v === '' ? '—' : `${Number(v).toFixed(dp)}%`;

// ---------------------------------------------------------------------------
// Contact emails — every university address in one export
// ---------------------------------------------------------------------------
/**
 * Three separate places hold an address — the office inbox on each technology,
 * the named tech manager, and the researcher — so pulling "the emails for a
 * university" used to be three queries and a manual merge. This is one file.
 *
 * Read live from Postgres rather than the SQLite catalogue the rest of this tab
 * uses: an address is the field most likely to have been corrected since that
 * snapshot was cut, and a stale one is a bounce.
 */
const ContactEmails: React.FC = () => {
  const { data, loading, error } = useAdminGet<any>('content', 'contact_emails');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [kind, setKind] = useState('');
  const [onlyValid, setOnlyValid] = useState(false);

  // Mirrors admin_contacts._KINDS; the workbook endpoint filters on the exact
  // string, so a kind missing here can only be reached by exporting everything.
  const KINDS = ['', 'Tech transfer office', 'Tech transfer office (curated)', 'Tech manager', 'Researcher'];
  const KIND_LABELS: Record<string, string> = {
    '': 'Everything', 'Tech transfer office': 'Tech transfer office only',
    'Tech transfer office (curated)': 'Curated TTO office only',
    'Tech manager': 'Tech managers only', 'Researcher': 'Researchers only',
  };

  const grab = async () => {
    setBusy(true); setMsg(null);
    const { data: wb, error: err } = await panelGet('content', 'contact_emails_workbook',
      { ...(kind ? { kind } : {}), only_valid: onlyValid ? '1' : '0' });
    setBusy(false);
    if (err || !wb?.content_b64) {
      setMsg({ kind: 'error', text: err || 'Export failed' });
      return;
    }
    // Base64 in the JSON envelope: the admin dispatcher wraps every response
    // and cannot stream a file body.
    const bin = atob(wb.content_b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    download(wb.filename || 'university_contacts.xlsx', bytes,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    setMsg({
      kind: 'ok',
      text: `${wb.filename} — ` + (wb.sheets || []).map((s: any) => `${s.sheet} ${s.rows}`).join(', ')
        + ` (${Math.round((wb.bytes || 0) / 1024)} KB).`,
    });
  };

  if (loading) return <Loading label="Counting addresses…" />;
  if (error) return <Msg kind="error">{error}</Msg>;

  return (
    <div className="flex flex-col gap-4">
      <KPIRow>
        <KPI label="Universities" value={data?.universities ?? '—'} />
        <KPI label="Addresses" value={data?.total_emails ?? '—'} hint="after splitting multi-address fields" />
        <KPI label="Distinct" value={data?.distinct_emails ?? '—'} />
        <KPI label="Malformed" value={data?.invalid ?? '—'} hint="exported and flagged, not dropped" />
      </KPIRow>

      <Section title="Export every address"
        right={<span className="text-xs text-grayscale-1100">
          {Object.entries(data?.by_kind || {}).map(([k, v]) => `${k}: ${v}`).join(' · ')}
        </span>}>
        <div className="flex gap-2 items-end flex-wrap">
          <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
            Include
            <Select value={kind} onChange={setKind} options={KINDS} labels={KIND_LABELS} />
          </label>
          <label className="flex items-center gap-2 text-xs text-grayscale-1200 pb-2 cursor-pointer">
            <input type="checkbox" checked={onlyValid}
              onChange={e => setOnlyValid(e.target.checked)} />
            Drop malformed addresses
          </label>
          <Btn kind="primary" disabled={busy} onClick={grab}>
            {busy ? 'Building…' : 'Download Excel — all universities'}
          </Btn>
        </div>
        {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
        <div className="text-xs text-grayscale-1100">
          One workbook: a summary per university, one sheet per kind, and a flat
          sheet of every address for a mail-merge. {data?.note}
        </div>
      </Section>

      <Section title="By university">
        <DataTable rows={data?.rows || []} columns={data?.columns || []} maxHeight="30rem" />
      </Section>
    </div>
  );
};

// BlobPart, not string: the contacts workbook is binary. Blob already
// accepts a Uint8Array, so this widens the type to what the function could
// always do rather than casting at the call site.
const download = (name: string, body: BlobPart, type: string) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([body], { type }));
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
};

const toCsv = (rows: Record<string, any>[], cols: string[]) => {
  const esc = (s: any) => `"${String(s ?? '').replace(/"/g, '""')}"`;
  return [cols.map(esc).join(',')]
    .concat(rows.map(r => cols.map(c => esc(r[c])).join(',')))
    .join('\n');
};

const Check: React.FC<{ checked: boolean; onChange: (v: boolean) => void; children: React.ReactNode }> =
  ({ checked, onChange, children }) => (
  <label className="flex items-center gap-2 text-xs text-grayscale-1100 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
      className="w-4 h-4 accent-primary-500" />
    {children}
  </label>
);

/** Renders the `error` string every catalog handler carries instead of 500ing. */
const HandlerError: React.FC<{ error?: string }> = ({ error }) =>
  error ? <Msg kind="error">{error}</Msg> : null;

// ---------------------------------------------------------------------------
const Overview: React.FC = () => {
  const stats = useAdminGet('content', 'stats');
  const breakdowns = useAdminGet('content', 'breakdowns');
  const b = breakdowns.data;
  return (
    <div className="flex flex-col gap-4">
      {stats.data && (
        <KPIRow>
          <KPI label="Technologies" value={stats.data.technologies?.toLocaleString?.()} />
          <KPI label="Universities" value={stats.data.universities} />
          <KPI label="Countries" value={stats.data.countries} />
          <KPI label="Researchers (with email)" value={stats.data.researchers?.toLocaleString?.()} />
          <KPI label="Tech managers" value={stats.data.tech_managers} />
        </KPIRow>
      )}
      {breakdowns.loading ? <Loading /> : b && (
        <div className="grid md:grid-cols-3 gap-4">
          <Section title="Technologies by country">
            <BarList items={Object.entries(b.by_country || {}).slice(0, 8)
              .map(([k, v]) => ({ label: k, value: v as number }))} />
          </Section>
          <Section title="Top industry categories">
            <BarList items={Object.entries(b.by_category || {})
              .map(([k, v]) => ({ label: k, value: v as number }))} />
          </Section>
          <Section title="TRL levels">
            <BarList items={Object.entries(b.by_trl || {})
              .map(([k, v]) => ({ label: k, value: v as number }))} />
          </Section>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
const UNI_VIEWS = ['Directory', 'Top 100', 'Countries', 'Common emails'];

const Universities: React.FC = () => {
  const [view, setView] = useState(UNI_VIEWS[0]);
  return (
    <div className="flex flex-col gap-4">
      <SubTabs tabs={UNI_VIEWS} active={view} onChange={setView} />
      {view === 'Directory' && <UniversityDirectory />}
      {view === 'Top 100' && <UniversityRollup />}
      {view === 'Countries' && <Countries />}
      {view === 'Common emails' && <CommonEmails />}
    </div>
  );
};

const UniversityDirectory: React.FC = () => {
  const [country, setCountry] = useState('All');
  const directory = useAdminGet('content', 'directory', { country }, [country]);
  const countries = Array.from(new Set(((directory.data || []) as any[]).map(r => r.country).filter(Boolean)));
  return (
    <Section title="University directory"
      right={<Select value={country} onChange={setCountry}
        options={['All', ...(country === 'All' ? countries : [...new Set([country, ...countries])])]} />}>
      {directory.error && <Msg kind="error">{directory.error}</Msg>}
      {directory.loading ? <Loading /> : (
        <DataTable rows={directory.data || []} searchable csvName="universities"
          columns={['university', 'country', 'tech_count', 'tto_email', 'cc_email', 'patents', 'papers', 'trl_data']}
          labels={{ tto_email: 'TTO email', cc_email: 'CC email', trl_data: 'TRL data' }}
          empty="No universities in the catalog." />
      )}
    </Section>
  );
};

const ROLLUP_LIMITS = ['25', '50', '100', '250', '1000'];

const UniversityRollup: React.FC = () => {
  const [limit, setLimit] = useState('100');
  const rollup = useAdminGet('content', 'university_rollup', { limit }, [limit]);
  const d = rollup.data;
  // The TOTAL row sums the RETURNED rows only (v2:5908-5915), so it rides
  // along in the table where the caption can say so.
  const rows = d?.rows ? [...d.rows, ...(d.totals?.university ? [d.totals] : [])] : [];
  return (
    <Section title="Top universities by technology count"
      right={<div className="flex items-center gap-2">
        <Select value={limit} onChange={setLimit} options={ROLLUP_LIMITS}
          labels={Object.fromEntries(ROLLUP_LIMITS.map(l => [l, `Top ${l}`]))} />
        <Btn small onClick={rollup.reload}>Refresh</Btn>
      </div>}>
      <HandlerError error={d?.error} />
      {rollup.error && <Msg kind="error">{rollup.error}</Msg>}
      {rollup.loading ? <Loading /> : (
        <>
          <DataTable rows={rows} searchable csvName="university_rollup"
            columns={['university', 'technologies', 'researchers', 'tech_managers', 'first_created', 'last_updated']}
            labels={{ tech_managers: 'Tech managers', first_created: 'First created', last_updated: 'Last updated' }}
            empty="No universities in the catalog."
            render={{
              technologies: r => num(r.technologies),
              researchers: r => num(r.researchers),
              university: r => r.university === 'TOTAL'
                ? <b className="text-grayscale-1300">TOTAL</b>
                : r.university,
            }} />
          <div className="text-xs text-grayscale-1100">
            The TOTAL row sums the {d?.limit ?? 0} rows shown, not the whole catalog — kept
            identical to the Streamlit tab so remembered numbers still match.
          </div>
        </>
      )}
    </Section>
  );
};

const Countries: React.FC = () => {
  const countries = useAdminGet('content', 'catalog_countries');
  const rows: any[] = countries.data?.rows || [];
  return (
    <div className="flex flex-col gap-4">
      <HandlerError error={countries.data?.error} />
      {countries.error && <Msg kind="error">{countries.error}</Msg>}
      {countries.loading ? <Loading /> : (
        <>
          <KPIRow>
            <KPI label="Countries" value={rows.length} />
            <KPI label="Universities" value={num(rows.reduce((a, r) => a + (r.universities || 0), 0))} />
            <KPI label="Technologies" value={num(rows.reduce((a, r) => a + (r.technologies || 0), 0))} />
            <KPI label="Researchers" value={num(rows.reduce((a, r) => a + (r.researchers || 0), 0))} />
          </KPIRow>
          <Section title="Technologies by country">
            <BarList items={rows.map(r => ({ label: r.country, value: r.technologies || 0 }))} />
          </Section>
          <Section title="Country rollup" right={<Btn small onClick={countries.reload}>Refresh</Btn>}>
            <DataTable rows={rows} csvName="catalog_countries"
              columns={['country', 'universities', 'technologies', 'researchers']}
              empty="No countries in the catalog." />
          </Section>
        </>
      )}
    </div>
  );
};

const CommonEmails: React.FC = () => {
  const emails = useAdminGet('content', 'catalog_common_emails', { limit: 5000 });
  const d = emails.data;
  const rows: any[] = (d?.rows || []).map((r: any) => ({ ...r, addresses: (r.addresses || []).join(', ') }));
  return (
    <div className="flex flex-col gap-4">
      <HandlerError error={d?.error} />
      {emails.error && <Msg kind="error">{emails.error}</Msg>}
      {emails.loading ? <Loading /> : (
        <>
          <KPIRow>
            <KPI label="Universities" value={d?.universities ?? 0} />
            <KPI label="Distinct mailboxes" value={d?.mailboxes ?? 0} />
            <KPI label="Pairs" value={rows.length} />
            <KPI label="Multi-address rows" value={d?.multi_address ?? 0}
              hint={d?.multi_address ? 'several addresses in one field' : undefined} />
          </KPIRow>
          <Section title="University common (TTO) mailboxes"
            right={<Btn small onClick={emails.reload}>Refresh</Btn>}>
            <p className="text-xs text-grayscale-1100 -mt-1">
              Every distinct (university, common_email) pair with the technologies behind it. The
              directory shows only the first address seen per university; this is where a mailbox
              that changed mid-crawl shows up.
            </p>
            <DataTable rows={rows} searchable csvName="common_emails"
              columns={['university', 'country', 'common_email', 'addresses', 'address_count', 'tech_count']}
              labels={{ common_email: 'Common email (raw)', address_count: 'Addresses', tech_count: 'Technologies' }}
              empty="No common email addresses recorded." />
          </Section>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
const PAGE = 50;

const TechExplorer: React.FC = () => {
  const universities = useAdminGet('content', 'universities');
  const [q, setQ] = useState('');
  const [applied, setApplied] = useState('');
  const [uni, setUni] = useState('');
  const [page, setPage] = useState(0);
  const res = useAdminGet('content', 'tech_search',
    { q: applied, university: uni, limit: PAGE, offset: page * PAGE },
    [applied, uni, page]);
  const [open, setOpen] = useState<string | null>(null);
  const total = res.data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / PAGE));
  return (
    <Section title={`Technology explorer (${total.toLocaleString()} matches)`}>
      <div className="flex gap-2 flex-wrap">
        <TextInput value={q} onChange={setQ} wide
          placeholder="Search title, UTech ID, or full text…" />
        <Select value={uni} onChange={v => { setUni(v); setPage(0); }}
          options={['', ...(universities.data || [])]} labels={{ '': 'All universities' }} />
        <Btn kind="primary" small onClick={() => { setApplied(q); setPage(0); }}>Search</Btn>
      </div>
      {res.loading ? <Loading /> : (
        <DataTable rows={res.data?.rows || []} csvName="technologies"
          columns={['utech_technology_id', 'title', 'university_name', 'university_country']}
          labels={{ utech_technology_id: 'UTech ID' }}
          render={{
            title: r => <span title={r.utech_summary || ''}>{r.title}</span>,
          }}
          actions={r => <Btn small onClick={() => setOpen(r.utech_technology_id)}>Open</Btn>} />
      )}
      <div className="flex items-center gap-2 justify-end text-xs text-grayscale-1100">
        <Btn small disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</Btn>
        Page {page + 1} of {pages}
        <Btn small disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)}>Next</Btn>
      </div>
      {open && <TechDetail techId={open} onClose={() => setOpen(null)} />}
    </Section>
  );
};

/** What was actually crawled for one technology — the legacy explorer's
 *  detail view (admin_panel_v2.py:7518-7601), which the table alone lost. */
const TechDetail: React.FC<{ techId: string; onClose: () => void }> = ({ techId, onClose }) => {
  const d = useAdminGet('content', 'tech_detail', { tech_id: techId }, [techId]);
  const [showText, setShowText] = useState(false);
  const t = d.data;
  const field = (label: string, value: any) => (
    <div className="flex gap-2 text-sm">
      <span className="text-grayscale-1100 w-36 shrink-0">{label}</span>
      <span className="text-grayscale-1300">{value || '—'}</span>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={onClose}>
      <div className="bg-grayscale-100 w-full max-w-2xl h-full overflow-y-auto p-6 flex flex-col gap-4"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-grayscale-1300 font-grotesk">{t?.title || techId}</h2>
            <div className="text-xs text-grayscale-1100">
              {techId} · {t?.university_name} {t?.university_country ? `· ${t.university_country}` : ''}
            </div>
          </div>
          <Btn onClick={onClose}>Close</Btn>
        </div>
        {d.error && <Msg kind="error">{d.error}</Msg>}
        {d.loading ? <Loading /> : !t || !t.utech_technology_id ? (
          <Msg kind="error">No catalog record for {techId}.</Msg>
        ) : (
          <>
            <Section title="Summary">
              <p className="text-sm text-grayscale-1300 whitespace-pre-wrap">{t.summary || '—'}</p>
            </Section>
            <Section title="Record">
              {field('Reference', t.reference_number)}
              {field('TTO email', t.common_email)}
              {field('Researchers', (t.researchers || []).join(', '))}
              {field('Keywords', t.keywords)}
              {field('Crawled', t.page_accessed_date)}
              {field('Page updated', t.page_updated_date)}
              {t.url && (
                <a href={t.url} target="_blank" rel="noreferrer"
                   className="text-sm text-primary-700 underline break-all">{t.url}</a>
              )}
            </Section>
            <Section title="TRL">
              {field('Original', t.original_trl)}
              {field('Calculated', t.calculated_trl)}
              {field('Confidence', t.trl_confidence)}
              <p className="text-sm text-grayscale-1300">{t.trl_statement || 'No reasoning recorded.'}</p>
            </Section>
            <Section title={`IP — ${(t.patents || []).length} patent(s), ${(t.research_papers || []).length} paper(s)`}>
              <p className="text-sm text-grayscale-1300">{t.ip_summary || 'No IP summary.'}</p>
              {t.publications_summary && (
                <p className="text-sm text-grayscale-1300">{t.publications_summary}</p>
              )}
            </Section>
            <Section title={`Crawled full text (${(t.full_text_length || 0).toLocaleString()} chars)`}
              right={<Btn small onClick={() => setShowText(s => !s)}>{showText ? 'Hide' : 'Show'}</Btn>}>
              {showText && (
                <div className="text-sm bg-white rounded-lg border border-grayscale-300 p-3 max-h-96 overflow-y-auto whitespace-pre-wrap">
                  {t.full_text || '—'}
                </div>
              )}
            </Section>
          </>
        )}
      </div>
    </div>
  );
};

const ResearcherExplorer: React.FC = () => {
  const universities = useAdminGet('content', 'universities');
  const [q, setQ] = useState('');
  const [applied, setApplied] = useState('');
  const [uni, setUni] = useState('');
  const [page, setPage] = useState(0);
  const res = useAdminGet('content', 'researcher_search',
    { q: applied, university: uni, limit: PAGE, offset: page * PAGE },
    [applied, uni, page]);
  const total = res.data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / PAGE));
  return (
    <Section title={`Researcher explorer (${total.toLocaleString()} matches)`}>
      <div className="flex gap-2 flex-wrap">
        <TextInput value={q} onChange={setQ} wide placeholder="Search name, email, or researcher ID…" />
        <Select value={uni} onChange={v => { setUni(v); setPage(0); }}
          options={['', ...(universities.data || [])]} labels={{ '': 'All universities' }} />
        <Btn kind="primary" small onClick={() => { setApplied(q); setPage(0); }}>Search</Btn>
      </div>
      {res.loading ? <Loading /> : (
        <DataTable rows={res.data?.rows || []} csvName="researchers"
          columns={['utech_researcher_id', 'researcher_name', 'university_name', 'email', 'contact_number']}
          labels={{ utech_researcher_id: 'ID' }} />
      )}
      <div className="flex items-center gap-2 justify-end text-xs text-grayscale-1100">
        <Btn small disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</Btn>
        Page {page + 1} of {pages}
        <Btn small disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)}>Next</Btn>
      </div>
    </Section>
  );
};

const TechManagers: React.FC = () => {
  const universities = useAdminGet('content', 'universities');
  const [uni, setUni] = useState('');
  const managers = useAdminGet('content', 'tech_managers', { university: uni }, [uni]);
  return (
    <Section title="Tech transfer office contacts"
      right={<Select value={uni} onChange={setUni}
        options={['', ...(universities.data || [])]} labels={{ '': 'All universities' }} />}>
      {managers.loading ? <Loading /> : (
        <DataTable rows={managers.data || []} searchable csvName="tech_managers"
          columns={['tech_manager_name', 'university_name', 'university_country', 'email', 'contact_number', 'url']} />
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Catalog DB — file status, headline metrics, the analytics build and history.
// ---------------------------------------------------------------------------
const CatalogDb: React.FC = () => {
  const status = useAdminGet('content', 'catalog_db_status');
  const metrics = useAdminGet('content', 'catalog_metrics');
  const history = useAdminGet('content', 'catalog_history', { limit: 500 });

  const [analytics, setAnalytics] = useState<any>(null);
  const [recordOnBuild, setRecordOnBuild] = useState(false);
  const [building, setBuilding] = useState(false);
  const [ack, setAck] = useState(false);
  const [snapping, setSnapping] = useState(false);
  const [msg, setMsg] = useState<Flash>(null);

  const st = status.data;
  const mt = metrics.data;
  const hist: any[] = history.data || [];

  const build = async () => {
    setBuilding(true); setMsg(null);
    const { data, error } = await panelGet('content', 'catalog_analytics',
      { snapshot: recordOnBuild ? 1 : 0, history_limit: 500 });
    setBuilding(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setAnalytics(data);
    if (data?.error) setMsg({ kind: 'error', text: data.error });
    else setMsg({ kind: 'ok', text: `Built in ${data?.metadata?.build_seconds ?? '?'}s.` });
    if (recordOnBuild) history.reload();
  };

  const snapshot = async () => {
    setSnapping(true); setMsg(null);
    const { data, error } = await panelPost('content', 'catalog_snapshot', {});
    setSnapping(false);
    setAck(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setMsg({
      kind: 'ok',
      text: data?.written
        ? `Snapshot recorded — history now holds ${data.entries} entries (latest: ${num(data.latest?.total_technologies)} technologies).`
        : 'No row written — the counters have not moved since the last snapshot.',
    });
    history.reload();
  };

  // Growth deltas against the previous snapshot; history arrives oldest-first.
  const histRows = hist.map((h, i) => ({
    timestamp: h.timestamp,
    total_technologies: h.total_technologies,
    d_tech: i ? h.total_technologies - hist[i - 1].total_technologies : null,
    total_researchers: h.total_researchers,
    d_res: i ? h.total_researchers - hist[i - 1].total_researchers : null,
    total_universities: h.total_universities,
    source: h.source,
  })).reverse();

  const meta = analytics?.metadata;
  const uniLedger: any[] = analytics?.universities || [];

  return (
    <div className="flex flex-col gap-4">
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}

      <Section title="SQLite catalog file" right={<Btn small onClick={status.reload}>Refresh</Btn>}>
        <HandlerError error={st?.error} />
        {status.error && <Msg kind="error">{status.error}</Msg>}
        {status.loading ? <Loading /> : st && (
          <>
            <KPIRow>
              <KPI label="Size" value={`${st.size_mb} MB`} />
              <KPI label="Tables" value={st.table_count} />
              <KPI label="SQLite" value={st.sqlite_version} />
              <KPI label="Readable" value={st.exists ? 'yes' : 'no'}
                hint={st.exists ? undefined : 'the API cannot open the catalog'} />
            </KPIRow>
            <div className="text-xs text-grayscale-1100 break-all">
              Path the API actually reads: <span className="text-grayscale-1300">{st.path}</span>
            </div>
            {st.env_override_differs && (
              <Msg kind="error">
                SQLITE_DB_PATH is set to <b>{st.env_override}</b>, which is not the file being
                read. Every number on this page comes from the path above.
              </Msg>
            )}
          </>
        )}
      </Section>

      <Section title="Key metrics" right={<Btn small onClick={metrics.reload}>Refresh</Btn>}>
        <HandlerError error={mt?.error} />
        {metrics.loading ? <Loading /> : mt && (
          <>
            <KPIRow>
              <KPI label="Technologies" value={num(mt.technologies)} />
              <KPI label="Researchers" value={num(mt.researchers)}
                hint={`${num(mt.researchers_contactable)} contactable`} />
              <KPI label="Tech managers" value={num(mt.tech_managers)}
                hint={`${num(mt.tech_managers_contactable)} with name + email`} />
              <KPI label="Universities" value={mt.universities} />
              <KPI label="Countries" value={mt.countries} />
            </KPIRow>
            <div className="text-xs text-grayscale-1100">
              Both counting definitions ship: the Overview tab reports researchers as
              <i> contactable</i> rows only, this tab counts every researcher row. Same for tech
              managers (all rows vs. name + email present).
            </div>
          </>
        )}
      </Section>

      <Section title="Catalog analytics builder"
        right={<div className="flex items-center gap-3 flex-wrap">
          <Check checked={recordOnBuild} onChange={setRecordOnBuild}>
            Record a history snapshot with this build
          </Check>
          <Btn kind="primary" small disabled={building} onClick={build}>
            {building ? 'Building…' : 'Build analytics'}
          </Btn>
        </div>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          One pass over the whole catalog (~235 KB of JSON) producing the per-university ledger,
          the country rollup and the full industry-category taxonomy. Leave the checkbox clear
          when you only want the export — a build with it set appends a permanent history row.
        </p>
        {!analytics && !building && (
          <div className="text-sm text-grayscale-1100 py-4 text-center">
            Not built yet — press <b>Build analytics</b>.
          </div>
        )}
        {building && <Loading label="Scanning the catalog…" />}
        {analytics && meta && (
          <>
            <KPIRow>
              <KPI label="Technologies" value={num(meta.total_technologies)} />
              <KPI label="Researchers" value={num(meta.total_researchers)} />
              <KPI label="Tech managers" value={num(meta.total_tech_managers)} />
              <KPI label="Universities" value={meta.total_universities} />
              <KPI label="Countries" value={meta.total_countries} />
              <KPI label="Categorised" value={num(meta.categorised_technologies)}
                hint={`built in ${meta.build_seconds}s`} />
            </KPIRow>
            {meta.scan_truncated && (
              <Msg kind="error">
                The category pass hit its row cap — the taxonomy below covers only the first
                {' '}{num(meta.categorised_technologies)} technologies.
              </Msg>
            )}
            <div className="flex items-center gap-2 flex-wrap text-xs text-grayscale-1100">
              Generated {fmtDate(meta.generated_at)} from {meta.database_path}
              <Btn small onClick={() => download(
                `catalog_analytics_${new Date().toISOString().slice(0, 10)}.json`,
                JSON.stringify(analytics, null, 2), 'application/json')}>
                Download JSON
              </Btn>
            </div>
            <DataTable rows={uniLedger} searchable csvName="catalog_universities"
              columns={['name', 'country', 'technologies', 'researchers', 'first_created', 'last_updated']}
              labels={{ name: 'University', first_created: 'First created', last_updated: 'Last updated' }}
              empty="The build returned no universities."
              render={{ technologies: r => num(r.technologies), researchers: r => num(r.researchers) }} />
          </>
        )}
      </Section>

      <Section title={`Snapshot history (${hist.length})`}
        right={<div className="flex items-center gap-3 flex-wrap">
          <Check checked={ack} onChange={setAck}>Write a permanent history row</Check>
          <Btn kind="primary" small disabled={!ack || snapping} onClick={snapshot}>
            {snapping ? 'Recording…' : 'Take snapshot now'}
          </Btn>
          <Btn small onClick={history.reload}>Refresh</Btn>
        </div>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          Automatic snapshots are deduped to at most one per day and are skipped when the
          counters have not moved. This button bypasses that, so tick the box first.
        </p>
        {history.error && <Msg kind="error">{history.error}</Msg>}
        {history.loading ? <Loading /> : (
          <>
            {hist.length > 1 && (
              <BarList items={hist.slice(-12).map(h => ({
                label: fmtDate(h.timestamp), value: h.total_technologies,
              }))} />
            )}
            <DataTable rows={histRows} csvName="catalog_history"
              columns={['timestamp', 'total_technologies', 'd_tech', 'total_researchers', 'd_res', 'total_universities', 'source']}
              labels={{
                total_technologies: 'Technologies', d_tech: 'Δ tech',
                total_researchers: 'Researchers', d_res: 'Δ researchers',
                total_universities: 'Universities',
              }}
              empty="No snapshots recorded yet."
              render={{
                total_technologies: r => num(r.total_technologies),
                total_researchers: r => num(r.total_researchers),
                d_tech: r => r.d_tech === null ? '—' : (r.d_tech > 0 ? `+${num(r.d_tech)}` : num(r.d_tech)),
                d_res: r => r.d_res === null ? '—' : (r.d_res > 0 ? `+${num(r.d_res)}` : num(r.d_res)),
              }} />
          </>
        )}
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Industry categories — global taxonomy, one university, and the comparison.
// ---------------------------------------------------------------------------
const Categories: React.FC = () => {
  const universities = useAdminGet('content', 'universities');
  const [uni, setUni] = useState('');
  const breakdown = useAdminGet('content', 'category_breakdown', { university: uni }, [uni]);
  const d = breakdown.data;
  const g = d?.global;
  const u = d?.uni;

  const catCols = ['category', 'technologies'];
  const cmpCols = ['category', 'global', 'university', 'pct_of_global'];
  const cmpLabels = { global: 'Global', university: uni || 'University', pct_of_global: '% of global' };

  return (
    <div className="flex flex-col gap-4">
      <Section title="Industry categories breakdown"
        right={<div className="flex items-center gap-2">
          <Select value={uni} onChange={setUni}
            options={['', ...(universities.data || [])]} labels={{ '': 'Global only' }} />
          <Btn small onClick={breakdown.reload}>Refresh</Btn>
        </div>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          Frequency over both taxonomy levels. A technology carries several categories, so the
          assignment totals exceed the technology count — they are not percentages of the corpus.
        </p>
        <HandlerError error={d?.error} />
        {breakdown.error && <Msg kind="error">{breakdown.error}</Msg>}
        {breakdown.loading ? <Loading /> : g && (
          <>
            <KPIRow>
              <KPI label="Major categories" value={num(g.major_categories)} />
              <KPI label="Major assignments" value={num(g.major_assignments)} />
              <KPI label="Subcategories" value={num(g.sub_categories)} />
              <KPI label="Sub assignments" value={num(g.sub_assignments)} />
              <KPI label="Rows scanned" value={num(d.scanned)}
                hint={d.truncated ? 'scan truncated at the row cap' : undefined} />
            </KPIRow>
            {d.truncated && (
              <Msg kind="error">
                The scan stopped at its row cap — these counts cover the first {num(d.scanned)}
                {' '}technologies only.
              </Msg>
            )}
          </>
        )}
      </Section>

      {!breakdown.loading && g && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Section title="Global — major categories">
            <BarList items={(g.major || []).slice(0, 12)
              .map((r: any) => ({ label: r.category, value: r.technologies }))} />
            <DataTable rows={g.major || []} searchable csvName="categories_major_global"
              columns={catCols} maxHeight="20rem" empty="No major categories found."
              render={{ technologies: r => num(r.technologies) }} />
          </Section>
          <Section title="Global — subcategories">
            <BarList items={(g.sub || []).slice(0, 12)
              .map((r: any) => ({ label: r.category, value: r.technologies }))} />
            <DataTable rows={g.sub || []} searchable csvName="categories_sub_global"
              columns={catCols} maxHeight="20rem" empty="No subcategories found."
              render={{ technologies: r => num(r.technologies) }} />
          </Section>
        </div>
      )}

      {!breakdown.loading && u && (
        <>
          <Section title={`${d.university} — its own taxonomy`}>
            <KPIRow>
              <KPI label="Major categories" value={num(u.major_categories)} />
              <KPI label="Major assignments" value={num(u.major_assignments)} />
              <KPI label="Subcategories" value={num(u.sub_categories)} />
              <KPI label="Sub assignments" value={num(u.sub_assignments)} />
            </KPIRow>
            <div className="grid lg:grid-cols-2 gap-4">
              <DataTable rows={u.major || []} searchable csvName="categories_major_university"
                columns={catCols} maxHeight="20rem" empty="No major categories for this university."
                render={{ technologies: r => num(r.technologies) }} />
              <DataTable rows={u.sub || []} searchable csvName="categories_sub_university"
                columns={catCols} maxHeight="20rem" empty="No subcategories for this university."
                render={{ technologies: r => num(r.technologies) }} />
            </div>
          </Section>
          <Section title="Share of the top 5 global categories">
            <p className="text-xs text-grayscale-1100 -mt-1">
              The five largest <i>global</i> entries with this university's share of each — the
              point is where it sits inside the global taxonomy, including the gaps.
            </p>
            <div className="grid lg:grid-cols-2 gap-4">
              <DataTable rows={d.comparison?.major || []} columns={cmpCols} labels={cmpLabels}
                empty="No comparison available."
                render={{ pct_of_global: r => pct(r.pct_of_global) }} />
              <DataTable rows={d.comparison?.sub || []} columns={cmpCols} labels={cmpLabels}
                empty="No comparison available."
                render={{ pct_of_global: r => pct(r.pct_of_global) }} />
            </div>
          </Section>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Data quality
// ---------------------------------------------------------------------------
const DQ_ROW_COLS = ['utech_technology_id', 'title', 'university_name', 'has_ip_summary',
  'patents', 'patents_valid', 'has_pub_summary', 'research_papers', 'research_papers_valid',
  'full_text_len'];

const DataQuality: React.FC = () => {
  const [thresholdInput, setThresholdInput] = useState('500');
  const [threshold, setThreshold] = useState('500');
  const [refresh, setRefresh] = useState(0);
  const [filter, setFilter] = useState('');
  const dq = useAdminGet('content', 'data_quality',
    { threshold, refresh: refresh ? 1 : 0 }, [threshold, refresh]);
  const d = dq.data;

  const filters: any[] = d?.filters || [];
  const active = filters.find(f => f.key === filter);
  const ft = d?.full_text;

  return (
    <div className="flex flex-col gap-4">
      {dq.error && <Msg kind="error">{dq.error}</Msg>}
      <HandlerError error={d?.error} />

      <Section title="Data quality stats"
        right={<Btn small onClick={() => setRefresh(r => r + 1)}>Refresh (rescan DB)</Btn>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          Coverage of IP summaries, patents, publication summaries, research papers and full text
          across all {num(d?.total)} technologies. Results are cached for five minutes; use
          Refresh after a crawl.
          {d?.computed_at && <> Computed {fmtDate(d.computed_at)}.</>}
        </p>
        {dq.loading ? <Loading label="Scanning the catalog…" /> : d?.empty ? (
          <div className="text-sm text-grayscale-1100 py-4 text-center">
            The catalog holds no technologies — nothing to measure.
          </div>
        ) : d && (
          <div className="flex flex-col gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-grayscale-1100 mb-1">IP summary</div>
              <KPIRow>
                <KPI label="With IP summary" value={num(d.ip_summary?.with)} hint={pct(d.ip_summary?.with_pct)} />
                <KPI label="Without" value={num(d.ip_summary?.without)} hint={pct(d.ip_summary?.without_pct)} />
                <KPI label="With patents" value={num(d.patents?.with)} />
                <KPI label="Without patents" value={num(d.patents?.without)} hint={pct(d.patents?.without_pct)} />
                <KPI label="Patents recorded" value={num(d.patents?.total)}
                  hint={`avg ${d.patents?.avg_when_present} when present`} />
              </KPIRow>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-grayscale-1100 mb-1">Publications & papers</div>
              <KPIRow>
                <KPI label="With pub summary" value={num(d.publications?.with)} hint={pct(d.publications?.with_pct)} />
                <KPI label="Without" value={num(d.publications?.without)} hint={pct(d.publications?.without_pct)} />
                <KPI label="Both summaries" value={num(d.publications?.both_summaries)} hint="IP + publications" />
                <KPI label="With research papers" value={num(d.research_papers?.with)} />
                <KPI label="Without papers" value={num(d.research_papers?.without)} hint={pct(d.research_papers?.without_pct)} />
                <KPI label="Papers recorded" value={num(d.research_papers?.total)}
                  hint={`avg ${d.research_papers?.avg_when_present} when present`} />
              </KPIRow>
            </div>
          </div>
        )}
      </Section>

      {!dq.loading && d && !d.empty && (
        <>
          <Section title="Full-text length"
            right={<div className="flex items-center gap-2">
              <span className="text-xs text-grayscale-1100">Threshold (chars)</span>
              <TextInput value={thresholdInput} onChange={setThresholdInput} type="number" />
              <Btn small kind="primary" onClick={() => setThreshold(thresholdInput || '0')}>Apply</Btn>
            </div>}>
            <KPIRow>
              <KPI label="Average length" value={num(ft?.avg_length)} />
              <KPI label="No full text" value={num(ft?.none)} hint={pct(ft?.none_pct)} />
              <KPI label={`Below ${num(ft?.threshold)} chars`} value={num(ft?.below_threshold)}
                hint={pct(ft?.below_threshold_pct)} />
            </KPIRow>
            <BarList total={d.total} items={[
              { label: 'No full text', value: ft?.none || 0 },
              { label: 'Under 100 chars', value: ft?.lt_100 || 0 },
              { label: '100 – 500 chars', value: ft?.b100_500 || 0 },
              { label: '500 – 1000 chars', value: ft?.b500_1000 || 0 },
            ]} />
            <div className="text-xs text-grayscale-1100">
              The buckets cascade and stop at 1000 — technologies with 1000+ characters land in
              none of them, exactly as the Streamlit tab reported.
            </div>
          </Section>

          <Section title="Drill down by filter">
            <div className="flex flex-col gap-2">
              {[['standard', 'Single'], ['cross', 'Cross-check']].map(([group, label]) => (
                <div key={group} className="flex gap-2 flex-wrap items-center">
                  <span className="text-[11px] uppercase tracking-wide text-grayscale-1100 w-24">{label}</span>
                  {filters.filter(f => f.group === group).map(f => (
                    <Btn key={f.key} small kind={f.key === filter ? 'primary' : 'subtle'}
                      onClick={() => setFilter(f.key === filter ? '' : f.key)}>
                      {f.label} · {num(f.count)}
                    </Btn>
                  ))}
                </div>
              ))}
            </div>
            {!filter && (
              <div className="text-sm text-grayscale-1100 py-4 text-center">
                Pick a filter to list the technologies behind its count.
              </div>
            )}
          </Section>

          {filter && <DataQualityRows filterKey={filter} label={active?.label || filter} />}

          <Section title={`Per-university statistics (${d.universities_count})`}>
            <DataTable rows={d.universities || []} searchable csvName="data_quality_universities"
              maxHeight="34rem"
              columns={['university', 'total_tech', 'no_ip_sum', 'no_ip_sum_pct', 'total_patents',
                'no_patents', 'no_pub_sum', 'no_pub_sum_pct', 'total_papers', 'no_papers',
                'no_full_text', 'avg_ft_len', 'below_threshold']}
              labels={{
                total_tech: 'Techs', no_ip_sum: 'No IP sum', no_ip_sum_pct: 'No IP %',
                total_patents: 'Patents', no_patents: 'No patents', no_pub_sum: 'No pub sum',
                no_pub_sum_pct: 'No pub %', total_papers: 'Papers', no_papers: 'No papers',
                no_full_text: 'No full text', avg_ft_len: 'Avg FT len',
                below_threshold: `< ${num(ft?.threshold)}c`,
              }}
              empty="No universities in the catalog."
              render={{
                total_tech: r => num(r.total_tech),
                total_patents: r => num(r.total_patents),
                total_papers: r => num(r.total_papers),
                avg_ft_len: r => num(r.avg_ft_len),
                no_ip_sum_pct: r => pct(r.no_ip_sum_pct, 0),
                no_pub_sum_pct: r => pct(r.no_pub_sum_pct, 0),
              }} />
          </Section>
        </>
      )}
    </div>
  );
};

const DQ_PAGE = 200;

const DataQualityRows: React.FC<{ filterKey: string; label: string }> = ({ filterKey, label }) => {
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [msg, setMsg] = useState<Flash>(null);
  const rows = useAdminGet('content', 'data_quality_rows',
    { filter: filterKey, limit: DQ_PAGE, offset: page * DQ_PAGE }, [filterKey, page]);
  const d = rows.data;
  const total = d?.total || 0;
  const pages = Math.max(1, Math.ceil(total / DQ_PAGE));

  // The on-screen table stays at 200 rows; the export pulls the complete
  // filtered set (the 20k cap exists for exactly this click).
  const exportAll = async () => {
    setExporting(true); setMsg(null);
    const { data, error } = await panelGet('content', 'data_quality_rows',
      { filter: filterKey, limit: 20000, offset: 0 });
    setExporting(false);
    if (error || data?.error) { setMsg({ kind: 'error', text: error || data.error }); return; }
    download(`data_quality_${filterKey}_${new Date().toISOString().slice(0, 10)}.csv`,
      toCsv(data.rows || [], DQ_ROW_COLS), 'text/csv');
    setMsg({ kind: 'ok', text: `Exported ${(data.rows || []).length} of ${data.total} row(s).` });
  };

  return (
    <Section title={`${label} — ${total.toLocaleString()} technologies`}
      right={<Btn small disabled={exporting} onClick={exportAll}>
        {exporting ? 'Building CSV…' : 'Download all matching rows'}
      </Btn>}>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      {rows.error && <Msg kind="error">{rows.error}</Msg>}
      <HandlerError error={d?.error} />
      {rows.loading ? <Loading /> : (
        <DataTable rows={d?.rows || []} searchable maxHeight="30rem"
          columns={DQ_ROW_COLS}
          labels={{
            utech_technology_id: 'UTech ID', university_name: 'University',
            has_ip_summary: 'IP sum', patents: 'Patents (raw)', patents_valid: 'Patents (valid)',
            has_pub_summary: 'Pub sum', research_papers: 'Papers (raw)',
            research_papers_valid: 'Papers (valid)', full_text_len: 'Full-text chars',
          }}
          empty="No technologies match this filter." />
      )}
      <div className="flex items-center gap-2 justify-between flex-wrap text-xs text-grayscale-1100">
        <span>
          Raw counts are the list lengths the legacy table printed; valid counts are the non-null
          entries the KPIs counted. They differ where a crawl wrote placeholder rows.
        </span>
        <span className="flex items-center gap-2">
          <Btn small disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</Btn>
          Page {page + 1} of {pages}
          <Btn small disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)}>Next</Btn>
        </span>
      </div>
    </Section>
  );
};

// ---------------------------------------------------------------------------
// TRL
// ---------------------------------------------------------------------------
const TRL_VIEWS = ['Overview', 'By university', 'University detail'];

const Trl: React.FC = () => {
  const [view, setView] = useState(TRL_VIEWS[0]);
  const [refresh, setRefresh] = useState(0);
  const overview = useAdminGet('content', 'trl_overview', { refresh: refresh ? 1 : 0 }, [refresh]);
  const byUni = useAdminGet('content', 'trl_by_university', { refresh: refresh ? 1 : 0 }, [refresh]);
  const o = overview.data;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <SubTabs tabs={TRL_VIEWS} active={view} onChange={setView} />
        <Btn small onClick={() => setRefresh(r => r + 1)}>Refresh (rescan DB)</Btn>
      </div>
      {overview.error && <Msg kind="error">{overview.error}</Msg>}
      <HandlerError error={o?.error} />

      {view === 'Overview' && (overview.loading ? <Loading label="Scanning development stages…" /> : o && (
        <>
          <Section title="Coverage">
            <KPIRow>
              <KPI label="Technologies" value={num(o.kpis?.total_technologies)} />
              <KPI label="With original TRL" value={num(o.kpis?.with_original_trl)} />
              <KPI label="With calculated TRL" value={num(o.kpis?.with_calculated_trl)} />
              <KPI label="High confidence (4–5)" value={num(o.kpis?.high_confidence_4_5)} />
              <KPI label="Low confidence (1–3)" value={num(o.kpis?.low_confidence_1_3)} />
            </KPIRow>
            <div className="text-xs text-grayscale-1100">
              Confidence “Score 0” is a recorded value, not a missing one — it counts in neither
              the high nor the low KPI. {o.computed_at && <>Computed {fmtDate(o.computed_at)}.</>}
            </div>
          </Section>
          <div className="grid lg:grid-cols-3 gap-4">
            <TrlDist title="Original TRL" bars={o.original || []} missing={o.original_missing} total={o.total} />
            <TrlDist title="Calculated TRL" bars={o.calculated || []} missing={o.calculated_missing} total={o.total} />
            <TrlDist title="Confidence score" bars={o.confidence || []} missing={o.confidence_missing} total={o.total} />
          </div>
        </>
      ))}

      {view === 'By university' && (
        <Section title={`TRL coverage by university (${byUni.data?.count ?? 0})`}>
          <p className="text-xs text-grayscale-1100 -mt-1">
            This aggregate drops non-numeric original TRLs; the overview and the single-university
            drill-down keep them, so the two tables legitimately disagree.
          </p>
          {byUni.error && <Msg kind="error">{byUni.error}</Msg>}
          <HandlerError error={byUni.data?.error} />
          {byUni.loading ? <Loading /> : (
            <DataTable rows={byUni.data?.rows || []} searchable csvName="trl_by_university"
              maxHeight="34rem"
              columns={['university', 'total_tech', 'no_orig_trl', 'no_orig_pct', 'most_common_orig',
                'no_calc_trl', 'no_calc_pct', 'most_common_calc', 'high_conf', 'high_conf_pct']}
              labels={{
                total_tech: 'Techs', no_orig_trl: 'No orig TRL', no_orig_pct: 'No orig %',
                most_common_orig: 'Most common orig', no_calc_trl: 'No calc TRL',
                no_calc_pct: 'No calc %', most_common_calc: 'Most common calc',
                high_conf: 'High conf', high_conf_pct: 'High conf %',
              }}
              empty="No universities with TRL data."
              render={{
                total_tech: r => num(r.total_tech),
                // Percentages arrive at 6dp on purpose: rounding twice moved
                // rows by a whole point against the legacy table.
                no_orig_pct: r => pct(r.no_orig_pct, 0),
                no_calc_pct: r => pct(r.no_calc_pct, 0),
                high_conf_pct: r => pct(r.high_conf_pct, 1),
              }} />
          )}
        </Section>
      )}

      {view === 'University detail' && (
        <TrlUniversityDetail universities={o?.universities || []} loading={overview.loading} />
      )}
    </div>
  );
};

const TrlDist: React.FC<{ title: string; bars: any[]; missing: any; total: number }> =
  ({ title, bars, missing, total }) => {
  const all = [...bars];
  if (missing) all.push({ key: '_missing', label: missing.label, count: missing.count, pct: missing.pct });
  const top = [...all].sort((a, b) => b.count - a.count).slice(0, 12);
  return (
    <Section title={title}>
      <BarList total={total} items={top.map(b => ({ label: b.label, value: b.count }))} />
      <DataTable rows={all} columns={['label', 'count', 'pct']} maxHeight="16rem"
        labels={{ label: 'Bucket', pct: 'Share' }}
        csvName={`trl_${title.toLowerCase().replace(/\s+/g, '_')}`}
        empty="No values recorded."
        render={{ count: r => num(r.count), pct: r => pct(r.pct) }} />
    </Section>
  );
};

const TRL_DETAIL_PAGE = 100;

const TrlUniversityDetail: React.FC<{ universities: string[]; loading: boolean }> =
  ({ universities, loading }) => {
  const [uni, setUni] = useState('');
  return (
    <div className="flex flex-col gap-4">
      <Section title="Single-university TRL drill-down"
        right={<Select value={uni} onChange={setUni} options={['', ...universities]}
          labels={{ '': 'Pick a university…' }} />}>
        {loading ? <Loading /> : !uni && (
          <div className="text-sm text-grayscale-1100 py-4 text-center">
            Pick a university to see its distributions and per-technology TRL rows.
          </div>
        )}
      </Section>
      {uni && <TrlDetailBody university={uni} />}
    </div>
  );
};

const TrlDetailBody: React.FC<{ university: string }> = ({ university }) => {
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState<any>(null);
  const detail = useAdminGet('content', 'trl_university_detail',
    { university, limit: TRL_DETAIL_PAGE, offset: page * TRL_DETAIL_PAGE }, [university, page]);
  const d = detail.data;
  const total = d?.total || 0;
  const pages = Math.max(1, Math.ceil(total / TRL_DETAIL_PAGE));

  const dist = (title: string, bars: any[], missingCount: number, missingLabel: string) => (
    <Section title={title}>
      <BarList total={total} items={[
        ...(bars || []).map((b: any) => ({ label: b.label, value: b.count })),
        { label: missingLabel, value: missingCount || 0 },
      ]} />
    </Section>
  );

  return (
    <div className="flex flex-col gap-4">
      {detail.error && <Msg kind="error">{detail.error}</Msg>}
      <HandlerError error={d?.error} />
      {detail.loading ? <Loading /> : d && (
        <>
          <KPIRow>
            <KPI label="Technologies" value={num(d.total)} />
            <KPI label="No original TRL" value={num(d.no_original)} />
            <KPI label="No calculated TRL" value={num(d.no_calculated)} />
            <KPI label="No confidence" value={num(d.no_confidence)} />
          </KPIRow>
          <div className="grid lg:grid-cols-3 gap-4">
            {dist('Original TRL', d.original, d.no_original, 'No Original TRL')}
            {dist('Calculated TRL', d.calculated, d.no_calculated, 'No Calculated TRL')}
            {dist('Confidence score', d.confidence, d.no_confidence, 'No Confidence Score')}
          </div>
          <Section title={`${d.university} — technologies`}>
            <DataTable rows={d.technologies || []} searchable csvName="trl_technologies"
              maxHeight="30rem"
              columns={['id', 'title', 'orig_trl', 'calc_trl', 'confidence', 'stage']}
              labels={{
                id: 'UTech ID', orig_trl: 'Original', calc_trl: 'Calculated',
                stage: 'Development stage',
              }}
              empty="No technologies for this university."
              actions={r => <Btn small onClick={() => setOpen(r)}>Reasoning</Btn>} />
            <div className="flex items-center gap-2 justify-end text-xs text-grayscale-1100">
              <Btn small disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</Btn>
              Page {page + 1} of {pages}
              <Btn small disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)}>Next</Btn>
            </div>
          </Section>
        </>
      )}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={() => setOpen(null)}>
          <div className="bg-grayscale-100 w-full max-w-xl h-full overflow-y-auto p-6 flex flex-col gap-4"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-grayscale-1300 font-grotesk">{open.title_full || open.title}</h2>
                <div className="text-xs text-grayscale-1100">{open.id}</div>
              </div>
              <Btn onClick={() => setOpen(null)}>Close</Btn>
            </div>
            <Section title="Assessment">
              <div className="text-sm text-grayscale-1300 flex flex-col gap-1">
                <div className="flex justify-between gap-4"><span>Original TRL</span><b>{open.orig_trl}</b></div>
                <div className="flex justify-between gap-4"><span>Calculated TRL</span><b>{open.calc_trl}</b></div>
                <div className="flex justify-between gap-4"><span>Confidence</span><b>{open.confidence}</b></div>
              </div>
            </Section>
            <Section title="Development stage">
              <div className="text-sm text-grayscale-1200 whitespace-pre-wrap break-words">
                {open.stage_full || '—'}
              </div>
            </Section>
            <Section title="Why this TRL">
              <div className="text-sm text-grayscale-1200 whitespace-pre-wrap break-words">
                {open.reasoning || 'The classifier recorded no reasoning for this technology.'}
              </div>
            </Section>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Crawl quality
// ---------------------------------------------------------------------------
const CQ_EXPORT_COLS = ['university', 'total_techs', 'ip_summary_be', 'publications_be',
  'trl_actual_be', 'trl_actual_evidence_be', 'trl_inferred_be', 'trl_inferred_evidence_be',
  'trl_not_disclosed_be', 'researchers_notnull_be', 'researchers_null_be', 'trl_shown_fe',
  'trl_not_disclosed_fe'];

const CrawlQuality: React.FC = () => {
  const [scope, setScope] = useState('');
  const [refresh, setRefresh] = useState(0);
  const cq = useAdminGet('content', 'crawl_quality',
    { university: scope, refresh: refresh ? 1 : 0 }, [scope, refresh]);
  const d = cq.data;
  const hiddenTotal = (d?.metrics || []).reduce((a: number, m: any) => a + (m.hidden || 0), 0);

  return (
    <div className="flex flex-col gap-4">
      {cq.error && <Msg kind="error">{cq.error}</Msg>}
      <HandlerError error={d?.error} />
      {d?.db_missing && (
        <Msg kind="error">The SQLite catalog is not readable at {d.db_path} — no crawl metrics.</Msg>
      )}

      <Section title="Backend vs frontend coverage"
        right={<div className="flex items-center gap-2">
          <Select value={scope} onChange={setScope}
            options={['', ...(d?.universities || [])]} labels={{ '': 'All universities' }} />
          <Btn small onClick={() => setRefresh(r => r + 1)}>Refresh (rescan DB)</Btn>
        </div>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          What the crawl stored against what the technology card actually shows. A gap means the
          database holds data the card hides. Metrics with no independent card element are marked
          “not on card” rather than counted as hidden.
        </p>
        {cq.loading ? <Loading label="Scanning the catalog…" /> : d?.empty ? (
          <div className="text-sm text-grayscale-1100 py-4 text-center">
            No technologies in scope — nothing to compare.
          </div>
        ) : d && (
          <>
            <KPIRow>
              <KPI label="Scope" value={d.scope} />
              <KPI label="Technologies in scope" value={num(d.scope_total)} />
              <KPI label="Catalog total" value={num(d.grand_total)} />
              <KPI label="Hidden from cards" value={num(hiddenTotal)}
                hint={hiddenTotal ? 'stored but not shown' : 'nothing withheld'} />
              <KPI label="Computed" value={fmtDate(d.cached_at)}
                hint={`cache ${d.ttl_seconds}s`} />
            </KPIRow>
            <DataTable rows={d.metrics || []} csvName="crawl_quality_metrics"
              columns={['label', 'backend', 'backend_pct', 'frontend', 'frontend_pct', 'gap']}
              labels={{
                label: 'Metric', backend: 'Backend', backend_pct: 'Backend %',
                frontend: 'Frontend', frontend_pct: 'Frontend %', gap: 'Gap',
              }}
              empty="No metrics for this scope."
              render={{
                backend: r => num(r.backend),
                backend_pct: r => pct(r.backend_pct),
                frontend: r => r.fe_applicable && r.frontend !== null
                  ? num(r.frontend)
                  : <span className="text-grayscale-1100">not on card</span>,
                frontend_pct: r => r.fe_applicable && r.frontend_pct !== null ? pct(r.frontend_pct) : '—',
                gap: r => r.gap === 'hidden'
                  ? <span className="text-red-600 font-medium">{num(r.hidden)} hidden</span>
                  : r.gap === 'match'
                    ? <span className="text-green-700">match</span>
                    : <span className="text-grayscale-1100">—</span>,
              }} />
            <div className="text-xs text-grayscale-1100">
              The inferred-TRL row can read as a huge frontend surplus: the card's gate
              (confidence ≥ 3) is far stricter than “has any TRL at all”, and a gap is only
              flagged when the frontend shows <i>fewer</i> rows than the backend holds.
            </div>
          </>
        )}
      </Section>

      {!cq.loading && d && !d.empty && (
        <>
          <Section title="Per-university completeness (backend %)">
            <DataTable rows={d.matrix || []} searchable csvName="crawl_quality_matrix"
              maxHeight="32rem"
              columns={['university', 'techs', 'ip_pct', 'pubs_pct', 'trl_actual_pct',
                'trl_inferred_pct', 'trl_shown_fe_pct', 'researchers_pct']}
              labels={{
                techs: 'Techs', ip_pct: 'IP summary', pubs_pct: 'Publications',
                trl_actual_pct: 'TRL actual', trl_inferred_pct: 'TRL inferred',
                trl_shown_fe_pct: 'TRL shown (FE)', researchers_pct: 'Researchers',
              }}
              empty="No universities in the catalog."
              render={{
                techs: r => num(r.techs),
                ip_pct: r => pct(r.ip_pct), pubs_pct: r => pct(r.pubs_pct),
                trl_actual_pct: r => pct(r.trl_actual_pct),
                trl_inferred_pct: r => pct(r.trl_inferred_pct),
                trl_shown_fe_pct: r => pct(r.trl_shown_fe_pct),
                researchers_pct: r => pct(r.researchers_pct),
              }} />
            <div className="text-xs text-grayscale-1100">
              Sorted by technology count; independent of the scope selector so you can read it to
              choose a scope.
            </div>
          </Section>

          <Section title="Raw per-university counts"
            right={<Btn small onClick={() => download(
              d.export_filename || 'crawl_quality.csv',
              toCsv(d.export || [], CQ_EXPORT_COLS), 'text/csv')}>
              Download CSV
            </Btn>}>
            <p className="text-xs text-grayscale-1100 -mt-1">
              The twelve columns the crawl-review sheet keys on, in their original order.
            </p>
            <DataTable rows={d.export || []} searchable maxHeight="32rem"
              columns={CQ_EXPORT_COLS}
              labels={{
                total_techs: 'Techs', ip_summary_be: 'IP sum', publications_be: 'Pubs',
                trl_actual_be: 'TRL actual', trl_actual_evidence_be: 'TRL actual evid.',
                trl_inferred_be: 'TRL inferred', trl_inferred_evidence_be: 'TRL inferred evid.',
                trl_not_disclosed_be: 'Not disclosed', researchers_notnull_be: 'Researchers',
                researchers_null_be: 'No researchers', trl_shown_fe: 'TRL shown (FE)',
                trl_not_disclosed_fe: 'Not disclosed (FE)',
              }}
              empty="No universities in the catalog."
              render={{ total_techs: r => num(r.total_techs) }} />
          </Section>
        </>
      )}
    </div>
  );
};

export default ContentPanel;
